/**
 * FinancialGoalsCard
 *
 * Dashboard widget showing active financial goals sorted by progress.
 * Improvements in [T38P0]:
 * • Goal-type badges (savings / debt / investment / other).
 * • Colour-coded progress bars (green near complete, amber mid, red behind pace).
 * • "Days left" countdown when a target date is present.
 * • Completed milestones counter.
 * • Responsive progress percentage chip aligned to the right.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Target, Flame, Clock, CheckCircle2, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Goal } from "@/lib/types";
import { useMemo } from "react";
import { differenceInDays, parseISO, isAfter, format } from "date-fns";
import { useCurrency } from "@/hooks/useCurrency";

interface FinancialGoalsCardProps {
  goals: Goal[];
}

const goalTypeConfig: Record<string, { label: string; className: string }> = {
  savings: {
    label: "Savings",
    className:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  },
  debt_payoff: {
    label: "Debt Payoff",
    className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  },
  investment: {
    label: "Investment",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  },
  other: {
    label: "Other",
    className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  },
};

/** Returns a TailwindCSS class for the progress bar based on % complete. */
function goalProgressColor(progress: number): string {
  if (progress >= 80) return "[&>div]:bg-green-500";
  if (progress >= 40) return "[&>div]:bg-amber-500";
  return "[&>div]:bg-blue-500";
}

export default function FinancialGoalsCard({ goals }: FinancialGoalsCardProps) {
  const { fmt } = useCurrency();
  const activeGoals = useMemo(
    () =>
      goals
        .filter((g) => g.status === "active")
        .sort((a, b) => {
          const pa = (a.currentAmount / a.targetAmount) * 100;
          const pb = (b.currentAmount / b.targetAmount) * 100;
          return pb - pa;
        })
        .slice(0, 3),
    [goals]
  );

  const completedCount = goals.filter((g) => g.status === "completed").length;

  return (
    <Card
      data-section="financial-goals"
      data-testid="home-card-progress-goals"
      className="transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <Target className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Financial Goals</CardTitle>
              {completedCount > 0 && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {completedCount} goal{completedCount > 1 ? "s" : ""} completed
                </p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary"
            asChild
          >
            <Link to="/goals" className="flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </>
      </CardHeader>
      <CardContent>
      <div className="space-y-5">
          {activeGoals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/60 mb-3">
                <Target className="h-7 w-7 text-muted-foreground" />
              </div>
              <h4 className="text-sm font-semibold text-foreground mb-1">
                No active goals
              </h4>
              <p className="text-xs text-muted-foreground mb-4 max-w-[190px]">
                Set savings targets and track your progress toward them.
              </p>
              <Button size="sm" asChild>
                <Link to="/goals" className="flex items-center gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  Create Your First Goal
                </Link>
              </Button>
            </div>
          ) : (
            activeGoals.map((goal) => {
              const progress = goal.targetAmount > 0
                ? Math.min((goal.currentAmount / goal.targetAmount) * 100, 100)
                : 0;
              const remaining = goal.targetAmount - goal.currentAmount;
              const typeConf =
                goalTypeConfig[goal.type] ?? goalTypeConfig.other;

              // Days-left calculation
              let daysLeft: number | null = null;
              let isOverdue = false;
              if (goal.targetDate) {
                const target = parseISO(goal.targetDate);
                const today = new Date();
                daysLeft = differenceInDays(target, today);
                isOverdue = !isAfter(target, today);
              }

              // Achieved milestones
              const achievedMilestones =
                goal.milestones?.filter((m) => m.achieved).length ?? 0;
              const totalMilestones = goal.milestones?.length ?? 0;

              return (
                <div key={goal.id} className="space-y-2">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-medium leading-tight truncate">
                          {goal.name}
                        </p>
                        <Badge
                          variant="secondary"
                          className={`border-0 text-[10px] px-1.5 py-0 h-4 leading-none whitespace-nowrap ${typeConf.className}`}
                        >
                          {typeConf.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {goal.targetDate && (
                          <span
                            className={`text-[10px] flex items-center gap-0.5 ${
                              isOverdue
                                ? "text-red-500"
                                : daysLeft !== null && daysLeft <= 30
                                ? "text-amber-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            {isOverdue ? (
                              <Flame className="h-3 w-3" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                            {isOverdue
                              ? "Overdue"
                              : daysLeft === 0
                              ? "Due today"
                              : daysLeft !== null
                              ? `${daysLeft}d left`
                              : format(parseISO(goal.targetDate), "MMM yyyy")}
                          </span>
                        )}
                        {totalMilestones > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            {achievedMilestones}/{totalMilestones} milestones
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Progress % chip */}
                    <div className="text-right shrink-0">
                      <p
                        className={`text-sm font-bold tabular-nums ${
                          progress >= 80
                            ? "text-green-600 dark:text-green-400"
                            : progress >= 40
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-blue-600 dark:text-blue-400"
                        }`}
                      >
                        {progress.toFixed(0)}%
                      </p>
                      <p className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {remaining > 0
                          ? `${fmt(remaining)} left`
                          : "Goal reached!"}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <Progress
                    value={progress}
                    className={`h-2 ${goalProgressColor(progress)}`}
                  />

                  {/* Amount */}
                  <p className="text-[10px] text-muted-foreground tabular-nums">
                    {fmt(goal.currentAmount)}{" "}
                    of {fmt(goal.targetAmount)}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
